import {
	DOMParser,
	DOMSerializer,
	Node,
	Schema
} from "prosemirror-model";
import { EditorState } from "prosemirror-state";
import { sanitizeHtml } from "client/Misc/htmlUtils";
import { isNil, isNotNilOrWhiteSpace } from "client/Misc/utils";

export function convertHtmlToProseMirrorNode(
	html: string,
	schema: Schema<never, never>
): Node {
	const newDom: Document = document.implementation
		.createHTMLDocument();
	newDom.write(
		sanitizeHtml(
			html,
			{ globalStylesBehavior: "remove" }
		)
	);
	newDom.close();

	return DOMParser.fromSchema(schema)
		.parse(newDom.body);
}

export function setHtml(
	editorState: EditorState,
	html: string,
	shouldAddToHistory: boolean = true
): EditorState {
	const newDoc: Node = convertHtmlToProseMirrorNode(html, editorState.schema);

	return editorState.apply(
		editorState.tr
			.setMeta("addToHistory", shouldAddToHistory)
			.replaceWith(
				0,
				editorState.doc.nodeSize - 2,
				newDoc
			)
	);
}

export function getHtml(editorState: EditorState): string {
	if (isEmptyContent(editorState, true)) {
		return "";
	}

	const dom: DocumentFragment | HTMLElement = DOMSerializer.fromSchema(editorState.schema)
		.serializeFragment(editorState.doc.content);

	const div: HTMLDivElement = document.createElement("div");
	div.appendChild(dom);

	return div.innerHTML;
}

export function isEmptyContent(
	editorState: EditorState,
	trimBeforeCheck = false
): boolean {
	const doc = editorState.doc;
	if (doc.childCount !== 1) {
		return false;
	};

	const firstChild: Node | null = doc.firstChild;
	if (isNil(firstChild) || !firstChild.isTextblock) {
		return false;
	};

	const hasNoContent: boolean = firstChild.content
		.size === 0;

	if (hasNoContent) {
		return true;
	};

	if (trimBeforeCheck) {
		let isEmpty: boolean = true;

		firstChild.content
			.forEach((node: Node): void => {
				if (!node.isText) {
					isEmpty = false;
					return;
				}

				if (isNotNilOrWhiteSpace(node.textContent)) {
					isEmpty = false;
				}
			});

		return isEmpty;
	}

	return false;
}
